import React, { useCallback, useEffect, useState } from "react";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import { addFavorite, removeFavorite, isAiActivated, fetchLatestForumFavorites } from "../../HTTP/articles";
import { formatDateExtended, getDomainName } from "../../Helper/utils";
import "../Home/articles.css";
import { FactChecking } from "../Home/factChecking";

const LIMIT = 20;

const SavedBy = ({ users, privateCount }) => {
  const others = privateCount > 0
    ? `${privateCount} ${users.length > 0 ? "autre" + (privateCount > 1 ? "s " : " ") : ""}utilisateur${privateCount > 1 ? "s" : ""}`
    : "";

  if (users.length === 0 && !others) return null;

  return (
    <>
      {users.length > 0 && "Enregistré par "}
      {users.map((u, i) => (
        <React.Fragment key={u.id}>
          {i > 0 && ", "}
          <a href={`/friends/${u.id}`} className="text-cyan-700 hover:underline">
            {`${u.firstname} ${u.lastname}`.trim()}
          </a>
        </React.Fragment>
      ))}
      {users.length > 0 && others && " et "}
      {others}
    </>
  );
};

const ForumFavorites = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const load = useCallback(async (offset) => {
    setLoading(true);
    setError(false);
    try {
      const res = await fetchLatestForumFavorites(LIMIT, offset);
      setArticles((prev) => (offset === 0 ? res : [...prev, ...res]));
      setHasMore(res.length >= LIMIT);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const [favorites, setFavorites] = useState(() => JSON.parse(localStorage.getItem("favorites") || "[]"));
  const [aiActivated, setAiActivated] = useState(false);
  const [factCheckLink, setFactCheckLink] = useState(null);

  useEffect(() => {
    load(0);
  }, [load]);

  useEffect(() => {
    isAiActivated()
      .then((res) => {
        if (res !== undefined) setAiActivated(res.value === "1");
      })
      .catch(() => {});
  }, []);

  const saveFavorites = (list) => {
    setFavorites(list);
    localStorage.setItem("favorites", JSON.stringify(list));
  };

  const toggleFavorite = async (article) => {
    try {
      if (favorites.some((f) => f.link === article.link)) {
        const res = await removeFavorite({ link: article.link });
        if (res) saveFavorites(favorites.filter((f) => f.link !== article.link));
      } else {
        const favorite = {
          title: article.title,
          link: article.link,
          image: article.image || "",
          country: article.country,
          publication_date: article.publication_date,
          description: article.description,
        };
        const res = await addFavorite(favorite);
        if (res && res.ok === true) saveFavorites([{ ...favorite, id: res.id }, ...favorites]);
      }
    } catch (e) {
      console.error("Favorite update failed", e);
      alert("Une erreur est survenue lors de la mise à jour des favoris");
    }
  };

  return (
    <div className="mb-8">
      <div className="flex justify-center text-center mb-4">
        <h2 className="mt-2 text-2xl font-bold">Articles enregistrés dans les forums globaux</h2>
      </div>

      {error && (
        <div className="text-center mb-4 text-red-600">
          Impossible de charger les articles.{" "}
          <button type="button" className="underline" onClick={() => load(articles.length)}>
            Réessayer
          </button>
        </div>
      )}

      {!loading && !error && articles.length === 0 && (
        <div className="text-center mb-4">Aucun article enregistré dans les forums</div>
      )}

      <div className="allArticlesDiv overflow-y-scroll" style={{ height: "60vh" }}>
        {articles.map((a) => {
          const isFav = favorites.some((f) => f.link === a.link);
          return (
            <div className="featuresfav" key={a.link}>
              <div className="itemfav rounded-lg shadow m-2">
                <div className="item-photowrapper">
                  <img
                    className="item-photofav"
                    src={a.image || process.env.PUBLIC_URL + "/news_icon2.jpg"}
                    alt="article"
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = process.env.PUBLIC_URL + "/news_icon2.jpg";
                    }}
                    onClick={() => window.open(a.link, "_blank")}
                  />
                </div>
                <div className="arttfav">
                  <div className="item-headerfav"></div>
                  <a href={a.link} target="_blank" rel="noreferrer" style={{ textDecoration: "inherit", color: "inherit" }}>
                    <h3 className="item-titlefav">{a.title}</h3>
                    <p>
                      <strong className="text-cyan-600">
                        Pays : {a.country} | Source : {getDomainName(a.link)} | {formatDateExtended(a.publication_date)}
                      </strong>
                    </p>
                    {a.description && <p className="item-textfav">{a.description}</p>}
                  </a>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(a.forums || []).map((f) => (
                      <a
                        key={f.id}
                        href={`/igo/${f.id}`}
                        className="bg-zemusbis text-white text-xs rounded-full px-2 py-1 hover:opacity-80"
                      >
                        {f.label}
                      </a>
                    ))}
                  </div>
                  <div className="text-xs text-gray-600 mt-1 mb-3">
                    <SavedBy users={a.users || []} privateCount={a.private_users_count || 0} />
                  </div>
                </div>
                <div className="actiondiv flex flex-col items-center justify-center gap-3 self-center">
                  <img
                    className="w-6 h-8 cursor-pointer"
                    src={process.env.PUBLIC_URL + (isFav ? "/bookmark.svg" : "/bookmarklight.svg")}
                    alt="favoris"
                    onClick={() => toggleFavorite(a)}
                  />
                  {aiActivated && (
                    <button
                      type="button"
                      className="w-8 h-8 rounded"
                      style={{ backgroundColor: "#CBE1E1" }}
                      title="Fact-check"
                      onClick={() => setFactCheckLink(a.link)}
                    >
                      <FactCheckIcon />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

      {factCheckLink && (
        <FactChecking articleLink={factCheckLink} setShowPopUpFactChecking={(show) => !show && setFactCheckLink(null)} />
      )}

      {loading && <div className="text-center my-4">Chargement...</div>}

      {!loading && !error && hasMore && articles.length > 0 && (
        <div className="flex justify-center mt-4">
          <button
            type="button"
            className="bg-zemusbis hover:bg-zemushover text-white font-bold py-2 px-4 rounded-md"
            onClick={() => load(articles.length)}
          >
            Voir plus
          </button>
        </div>
      )}
      </div>
    </div>
  );
};

export default ForumFavorites;
